import { ChangeDetectorRef, Component, computed, DestroyRef, ElementRef, Inject, inject, NgZone, OnDestroy, OnInit, AfterViewInit, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from "@angular/material/bottom-sheet";
import { AuthService } from "@core/auth.service";
import { CaptchaService } from "@core/captcha.service";
import { LoadingService } from "@core/loading.service";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { Observable, Subscription, interval } from "rxjs";
import { IonicModule } from "@ionic/angular";
import { NgClass } from "@angular/common";
import { MatProgressSpinner } from "@angular/material/progress-spinner";

type WebOtpCredential = Credential & { code?: string };

@Component({
  selector: "app-verify-phone-number",
  templateUrl: "./verify-phone-number.component.html",
  styleUrls: ["./verify-phone-number.component.scss"],
  standalone: true,
  imports: [
    IonicModule,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    MatProgressSpinner,
    TranslatePipe,
  ],
})
export class VerifyPhoneNumberComponent implements OnInit, AfterViewInit, OnDestroy {
  verifyPhoneCodeForm: FormGroup;
  @ViewChild("firstInput") field1;
  @ViewChild("timer", { static: false }) timerspan: ElementRef;
  @ViewChild("otpForm", { static: false }) otpForm?: ElementRef<HTMLFormElement>;
  private webOtpAbort: AbortController | null = null;
  private readonly destroyRef = inject(DestroyRef);
  private destroyed = false;
  private readonly otpControlNames = ["code1", "code2", "code3", "code4"] as const;
  timeOut: boolean = false;
  translatedToastMsg: string;
  spinnerAnimation: boolean;
  progressBar: number;
  proceed = true;
  // True while a verify request is in flight. Only the request callbacks (and a
  // resend) may clear it, otherwise a browser that autofills more than one field
  // would submit the same code twice.
  private verifying = false;
  timer$: Observable<number>;
  sub: Subscription;
  hideOtpInput: boolean;
  selectedLanguage: string = "en";
  fromHome: boolean;
  timerId: any;
  private disableCTA = false;
  isEmailValid = computed(() => {
    const emailValue = this.data?.msn;
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(emailValue); // Simple email regex validation
  });
  constructor(
    private matBottomSheetRef: MatBottomSheetRef<VerifyPhoneNumberComponent>,
    private fb: FormBuilder,
    private captchaService: CaptchaService,
    public translate: TranslateService,
    private authService: AuthService,
    private recaptchaService: CaptchaService,
    private loadingService: LoadingService,
    private changeDetectorRef: ChangeDetectorRef,
    private zone: NgZone,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any
  ) {
    this.authService?.selectedlanguage$?.subscribe(
      (l) => {
        this.selectedLanguage = l;
        translate.use(l ? l : "en");
      },
      (err) => {
        return err;
      }
    );
    this.authService.getConstantsMsg$?.subscribe((msg: any) => {
      this.translatedToastMsg = msg?.static_messages_pwa;
    });
    this.formDeclaration();
    this.hideOtpInput = false;
    this.fromHome = this.data?.from === "home";
  }

  ngOnInit() {
    this.formDeclaration();
    this.verifyPhoneCodeForm.statusChanges?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status) => {
      if (status === "VALID" && !this.verifying) {
        const code = this.otpControlNames
          .map((controlName) => this.verifyPhoneCodeForm.get(controlName)?.value ?? "")
          .join("");
        const isUpdateMsn = this.data?.type === "updateMsn";
        const isSignin = this.data?.type === "signin" && !!this.data?.msn;

        if (code.length === 4 && (isUpdateMsn || isSignin)) {
          this.verifying = true;
          if (isUpdateMsn) {
            this.verifyUpdateMsnCode(code);
          } else {
            this.verifySigninCode(code);
          }
        }
      }
      setTimeout(() => {
        if (this.destroyed) {
          return;
        }
        this.spinnerAnimation = false;
        this.proceed = false;
        this.detectChanges();
        setTimeout(() => {
          if (this.destroyed) {
            return;
          }
          this.proceed = true;
        }, 2000);
      }, 3000);
    });

    setTimeout(() => {
      this.timer(597);
    }, 2000);

    this.requestWebOtp();
  }

  ngAfterViewInit() {
    setTimeout(() => this.focusOtpField(0), 500);
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.timerOn = false;
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    this.sub?.unsubscribe();
    this.webOtpAbort?.abort();
    this.webOtpAbort = null;
  }

  private verifyUpdateMsnCode(code: string): void {
    this.captchaService.verifyCode(code, this.data.msn).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data) => {
        const res = (data as any)?.result ?? data;
        if (res && res?.data && res?.data?.flag === "true") {
          this.matBottomSheetRef.dismiss(true);
          return;
        }
        this.verifying = false;
        this.matBottomSheetRef.dismiss(false);
      },
      () => {
        // Without this the verifying flag would stay set and block every retry.
        this.verifying = false;
        this.hideOtpInput = true;
        this.detectChanges();
        this.showOtpErrorToast("We couldn't verify the code. Try again or request a new code.");
      }
    );
  }

  private verifySigninCode(code: string): void {
    this.captchaService.verifyCode(code, this.data.msn).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        const res = data?.result ?? data;
        const isVerified = res && (res?.status === "success" || (res?.data && (res?.data?.flag === "true" || res?.data?.status === "success")));
        const resetToken = res?.resetToken;
        if (isVerified && resetToken) {
          this.matBottomSheetRef.dismiss(res);
          return;
        }
        if (isVerified && !resetToken) {
          this.verifying = false;
          this.hideOtpInput = true;
          this.showOtpErrorToast("Something went wrong");
          return;
        }
        this.hideOtpInput = true;
        this.verifying = false;
        if (!this.isEmailValid()) {
          this.showOtpErrorToast("We couldn't verify the code. Try again or request a new code.");
        }
      },
      () => {
        this.hideOtpInput = true;
        this.verifying = false;
        this.showOtpErrorToast("We couldn't verify the code. Try again or request a new code.");
      }
    );
  }

  private showOtpErrorToast(line1: string): void {
    if (this.destroyed) {
      return;
    }
    this.authService.getConstantsMsg("1014").then((translatedMsg) => {
      // The sheet can be dismissed while the message lookup is in flight.
      if (this.destroyed) {
        return;
      }
      const defaultMsg = {
        message: {
          line_1: line1,
          line_2: "",
        },
        style: {
          type: "Error",
          icon: "tick circle error.svg",
        },
      };
      this.authService.showToastMessage(translatedMsg, defaultMsg);
    })
      .catch((error) => {
        return error;
      });
  }

  private formDeclaration() {
    this.verifyPhoneCodeForm = this.fb.group({
      // code: ["", Validators.compose([Validators.required, Validators.minLength(4), Validators.maxLength(4)])],
      code1: ["", Validators.compose([Validators.required, Validators.minLength(1), Validators.maxLength(1)])],
      code2: ["", Validators.compose([Validators.required, Validators.minLength(1), Validators.maxLength(1)])],
      code3: ["", Validators.compose([Validators.required, Validators.minLength(1), Validators.maxLength(1)])],
      code4: ["", Validators.compose([Validators.required, Validators.minLength(1), Validators.maxLength(1)])],
    });
  }

  backToLogin() {
    this.matBottomSheetRef.dismiss();
  }

  onSubmit() {
    // this.verifyPhoneCodeForm.reset()
  }

  focus(nextFocus, val) {
    if (val.value) {
      nextFocus.setFocus();
    }
  }

  resendOtp() {
    if (!this.resendAllowed) {
      return;
    }
    if (!this.disableCTA) {
      this.disableCTA = true;
      if (this.timerId) {
        clearTimeout(this.timerId);
        this.timerId = null;
      }
      // The spinner from the previous attempt keeps ticking otherwise, and
      // startSpinner() would leak that subscription on the next complete code.
      this.sub?.unsubscribe();
      this.sub = null;
      this.spinnerAnimation = false;
      this.progressBar = 0;
      this.verifyPhoneCodeForm.reset();
      this.verifying = false;
      this.loadingService.present();
      this.hideOtpInput = false;
      setTimeout(() => {
        this.recaptchaService.sendLoginCode(this.data.msn).then((res: any) => {
          if (res?.status === "success") {
            this.disableCTA = false;
            this.loadingService.dismiss();
            this.timeOut = false;
            this.timerOn = true;
            this.timer(597);
            this.requestWebOtp();
            this.focusOtpField(0);
            return;
          }

          this.handleResendOtpError(res);
        })
          .catch((error) => {
            this.handleResendOtpError(error);
            return error;
          });
      }, 1000);
    }
  }

  timerOn = true;

  get resendAllowed(): boolean {
    return this.timeOut && !this.disableCTA;
  }

  timer(remaining) {
    var m = Math.floor(remaining / 60);
    var s = remaining % 60;

    const mStr = m < 10 ? "0" + m : String(m);
    const sStr = s < 10 ? "0" + s : String(s);
    if (this.timerspan) {
      this.timerspan.nativeElement.innerHTML = mStr + ":" + sStr;
    }
    remaining -= 1;

    if (remaining >= 0 && this.timerOn) {
      this.timerId = setTimeout(() => {
        this.timer(remaining);
      }, 1000);
      return;
    }

    if (!this.timerOn) {
      // Do validate stuff here
      return;
    }

    // Do timeout stuff here
    this.timeOut = true;
  }

  private handleResendOtpError(error?: any) {
    this.disableCTA = false;
    this.loadingService.dismiss();
    this.timeOut = true;

    const apiMessage = error?.message || "Something went wrong";
    const errorCode = error?.error_code;

    if (errorCode) {
      this.authService.getConstantsMsg(errorCode).then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: apiMessage,
            line_2: "",
          },
          style: {
            type: "Error",
            icon: "tick circle error.svg",
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      })
        .catch(() => {
          this.showResendErrorToast(apiMessage);
        });
      return;
    }

    this.showResendErrorToast(apiMessage);
  }

  private showResendErrorToast(message: string) {
    const defaultMsg = {
      message: {
        line_1: message,
        line_2: "",
      },
      style: {
        type: "Error",
        icon: "tick circle error.svg",
      },
    };
    this.authService.showToastMessage({}, defaultMsg);
  }

  close() {
    this.matBottomSheetRef.dismiss();
  }
  startSpinner() {
    this.spinnerAnimation = true;
    this.progressBar = 0;
    this.timer$ = interval(20);
    this.sub = this.timer$?.subscribe(
      (sec) => {
        this.progressBar = 0 + sec;
        this.detectChanges();
      },
      (err) => {
        return err;
      }
    );
  }
  onOtpKeydown(event: KeyboardEvent, index: number): void {
    const key = event.key;
    const lastIndex = this.otpControlNames.length - 1;

    if (key === "Backspace" || event.keyCode === 8) {
      const input = event.target as HTMLInputElement;
      if (input?.value) {
        // Let the browser clear this box; handleOtpInput syncs the control.
        return;
      }
      event.preventDefault();
      if (index > 0) {
        this.setOtpDigit(index - 1, "");
        this.focusOtpField(index - 1);
      }
      return;
    }

    if (key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      this.focusOtpField(index - 1);
      return;
    }

    if (key === "ArrowRight" && index < lastIndex) {
      event.preventDefault();
      this.focusOtpField(index + 1);
    }
  }

  onOtpFocus(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    // Selecting on focus makes typing over a filled box replace the digit.
    setTimeout(() => input?.select?.(), 0);
  }

  onOtpAutofillCapture(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input?.value?.replace(/\D/g, "") || "";

    if (value.length < this.otpControlNames.length) {
      return;
    }

    this.distributeOtpDigits(value.substring(0, this.otpControlNames.length).split(""));
    // Hidden capture only — reset so a later SMS/resend can autofill again.
    if (input) {
      input.value = "";
    }
  }

  onOtpPaste(event: ClipboardEvent, index: number): void {
    const pastedData = event.clipboardData?.getData("text")?.replace(/\D/g, "") || "";
    if (!pastedData) {
      return;
    }

    event.preventDefault();

    if (pastedData.length >= this.otpControlNames.length) {
      this.distributeOtpDigits(pastedData.slice(0, this.otpControlNames.length).split(""));
      return;
    }

    this.fillOtpFrom(index, pastedData.split(""));
  }

  handleOtpInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const digits = (input?.value || "").replace(/\D/g, "");

    if (!digits) {
      // Keep the control in step with the box the user just emptied.
      this.setOtpDigit(index, "");
      return;
    }

    if (digits.length >= this.otpControlNames.length) {
      this.distributeOtpDigits(digits.slice(0, this.otpControlNames.length).split(""));
      return;
    }

    this.fillOtpFrom(index, digits.split(""));
  }

  /**
   * A single box can receive more than one digit from a partial autofill, a short
   * paste or fast typing, so spread what arrived across this box and the next ones.
   */
  private fillOtpFrom(index: number, digits: string[]): void {
    const lastIndex = this.otpControlNames.length - 1;
    const usable = digits.slice(0, this.otpControlNames.length - index);
    usable.forEach((digit, offset) => this.setOtpDigit(index + offset, digit));

    const filledIndex = index + usable.length - 1;
    if (filledIndex < lastIndex) {
      this.focusOtpField(filledIndex + 1);
    }

    this.commitOtpValue();
  }

  private distributeOtpDigits(digits: string[]): void {
    this.otpControlNames.forEach((_, index) => this.setOtpDigit(index, digits[index] || ""));

    this.focusOtpField(this.otpControlNames.length - 1);
    // Paint the filled boxes before verification, which may dismiss the sheet.
    this.detectChanges();
    this.commitOtpValue();
  }

  private detectChanges(): void {
    if (this.destroyed) {
      return;
    }
    this.changeDetectorRef.detectChanges();
  }

  private setOtpDigit(index: number, digit: string): void {
    const control = this.verifyPhoneCodeForm.get(this.otpControlNames[index]);
    if (!control) {
      return;
    }

    control.setValue(digit, { emitEvent: false });
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: false });
  }

  private isOtpComplete(): boolean {
    return this.otpControlNames.every(
      (controlName) => String(this.verifyPhoneCodeForm.get(controlName)?.value || "").length === 1
    );
  }

  /**
   * Digits are written with emitEvent: false so a code arriving in one go raises a
   * single status change instead of one per box, then verification runs once.
   */
  private commitOtpValue(): void {
    if (!this.isOtpComplete()) {
      return;
    }

    this.sub?.unsubscribe();
    this.progressBar = 0;
    this.startSpinner();
    this.verifyPhoneCodeForm.updateValueAndValidity({ emitEvent: true });
  }

  private focusOtpField(index: number): void {
    const fields = this.otpForm?.nativeElement?.querySelectorAll(".big-input");
    const field = fields?.[index] as HTMLInputElement | undefined;
    if (!field) {
      return;
    }

    field.focus();
    field.select?.();
  }

  private requestWebOtp(): void {
    if (this.destroyed) {
      return;
    }

    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return;
    }

    if (!("OTPCredential" in window) || typeof navigator.credentials?.get !== "function") {
      return;
    }

    this.webOtpAbort?.abort();
    this.webOtpAbort = new AbortController();

    navigator.credentials
      .get({
        otp: { transport: ["sms"] },
        signal: this.webOtpAbort.signal,
      } as CredentialRequestOptions)
      .then((credential) => {
        const code = (credential as WebOtpCredential)?.code?.replace(/\D/g, "") || "";
        if (this.destroyed || code.length < this.otpControlNames.length) {
          return;
        }

        this.zone.run(() => {
          this.distributeOtpDigits(code.substring(0, this.otpControlNames.length).split(""));
        });
      })
      .catch((err) => {
        if (err?.name === "AbortError") {
          return;
        }
        this.authService.errorHandlerLogs("[WebOTP] Failed:", err);
      });
  }
}
